
import { useParams } from 'react-router-dom';
import { useUser, useFirestore, useDoc, useMemoFirebase } from '@/firebase';
import { doc, DocumentReference } from 'firebase/firestore';
import type { Itinerary } from '@/app/lib/types';
import { ItineraryDetailsSkeleton } from '@/components/itinerary/details/ItineraryDetailsSkeleton';
import { ItineraryView } from '@/components/itinerary/ItineraryView';
import { ItineraryEditor } from '@/components/itinerary/ItineraryEditor';
import { DebugState } from '@/components/itinerary/details/DebugState';

/**
 * Props for the presentational SavedItineraryView component.
 * This allows the component to be rendered with any data source.
 */
export interface SavedItineraryViewProps {
  /** The itinerary data to display */
  itinerary: Itinerary | null;
  /** Reference to the Firestore document for editing */
  itineraryRef: DocumentReference | null;
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Any error that occurred during loading */
  error?: Error | null;
  /** Debug information for development */
  debugInfo?: {
    id: string;
    user: any;
    isUserLoading: boolean;
    isItineraryLoading: boolean;
  };
}

/**
 * Presentational component for displaying a saved itinerary.
 * Receives all data via props, making it easy to test and use in Storybook.
 */
export function SavedItineraryView({
  itinerary,
  itineraryRef,
  isLoading,
  error,
  debugInfo,
}: SavedItineraryViewProps) {
  // Show loading skeleton while data is being fetched
  if (isLoading) {
    return <ItineraryDetailsSkeleton />;
  }

  // Show debug state if no itinerary is available after loading
  if (!itinerary) {
    return (
      <DebugState
        id={debugInfo?.id ?? 'unknown'}
        user={debugInfo?.user ?? null}
        isUserLoading={debugInfo?.isUserLoading ?? false}
        itineraryRef={itineraryRef}
        itinerary={itinerary}
        isItineraryLoading={debugInfo?.isItineraryLoading ?? false}
        isLoading={isLoading}
        error={error ?? null}
      />
    );
  }

  // Show error state if we have an itinerary but no ref (shouldn't happen in practice)
  if (!itineraryRef) {
    return (
      <div className="container mx-auto">
        <p>Something went wrong. Itinerary data could not be loaded.</p>
      </div>
    );
  }

  // Render the full itinerary view with editor
  return (
    <div>
      <ItineraryView itinerary={itinerary} isEditable={true} />
      <div className="w-full max-w-7xl mx-auto -mx-3 sm:mx-auto px-0 sm:px-6 lg:px-8">
        <ItineraryEditor
          itineraryRef={itineraryRef}
          initialNotes={itinerary.notes || []}
          initialTodos={itinerary.todos || []}
        />
      </div>
    </div>
  );
}

/**
 * Container component that handles data fetching for SavedItineraryView.
 * Uses Firebase hooks to fetch the itinerary and passes data to the view.
 */
export function SavedItinerary() {
  const params = useParams();
  const id = params.id as string;
  const { user, isUserLoading } = useUser();
  const firestore = useFirestore();

  // A reference to the itinerary document.
  // This is memoized to prevent re-creating the reference on every render.
  const itineraryRef = useMemoFirebase(() => {
    if (!user || !firestore || !id) return null;
    return doc(firestore, 'users', user.uid, 'itineraries', id);
  }, [user, firestore, id]);

  // isRefReady is true only when we have a user and an id to build the ref.
  const isRefReady = !!itineraryRef;

  // Fetch the document using the reference.
  // The hook will only start fetching when itineraryRef is not null.
  const { data: itinerary, isLoading: isItineraryLoading, error } = useDoc<Itinerary>(itineraryRef);

  // The overall loading state for the page.
  // We are loading if the user is loading, or if the ref is ready but the itinerary hasn't been loaded yet.
  const isLoading = isUserLoading || (isRefReady && isItineraryLoading);

  return (
    <SavedItineraryView
      itinerary={itinerary}
      itineraryRef={itineraryRef}
      isLoading={isLoading}
      error={error}
      debugInfo={{
        id,
        user,
        isUserLoading,
        isItineraryLoading,
      }}
    />
  );
}
